/**
 * Sticky Add to cart bar.
 *
 * Three jobs, none of which own any state of their own:
 *
 *  - Visibility. An IntersectionObserver on the real Add to cart button shows
 *    the bar exactly while that button is off screen, and a second one on the
 *    footer takes it back down once the reader has reached the end of the page.
 *  - The button. It scrolls back to the real Add to cart and focuses it, rather
 *    than adding to the cart from down here — the variant and quantity are up
 *    there, and so is the feedback once the add is made.
 *  - Contents. A MutationObserver on the product info column copies the price,
 *    the chosen options and the real button's label across. Reading the
 *    finished DOM rather than the variant-change event means every path that
 *    touches the real button — a sold-out variant, an unavailable combination —
 *    lands here too, with no second copy of the rules that produced it.
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.trigger = document.querySelector(this.dataset.trigger);
        this.source = document.querySelector(this.dataset.source);
        this.priceSource = document.querySelector(this.dataset.price);

        this.button = this.querySelector('[data-sticky-action]');
        this.buttonText = this.querySelector('[data-sticky-action-text]');
        this.variantText = this.querySelector('[data-sticky-variant]');
        this.priceTarget = this.querySelector('[data-sticky-price]');

        // No real button to shadow: a bar that could fall out of step with the
        // form is worse than no bar, so it stays off.
        if (!this.trigger || !this.source) return;

        this.pastTrigger = false;
        this.atStop = false;

        this.button.addEventListener('click', () => this.goToForm());

        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            this.pastTrigger = !entry.isIntersecting;
            this.toggle();
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(this.trigger);

        // The footer is the end of the page: a bar pinned over it has nothing
        // left to follow, and it would cover the links the reader came down for.
        const stop = this.dataset.stop && document.querySelector(this.dataset.stop);
        if (stop) {
          this.stopObserver = new IntersectionObserver(
            ([entry]) => {
              this.atStop = entry.isIntersecting;
              this.toggle();
            },
            { threshold: 0 }
          );
          this.stopObserver.observe(stop);
        }

        // The bar sits outside this column, so its own updates cannot retrigger
        // the observer.
        this.contentObserver = new MutationObserver(() => this.sync());
        this.contentObserver.observe(this.source, {
          subtree: true,
          childList: true,
          attributes: true,
          characterData: true,
        });

        this.sync();
      }

      disconnectedCallback() {
        this.visibilityObserver?.disconnect();
        this.stopObserver?.disconnect();
        this.contentObserver?.disconnect();
      }

      goToForm() {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.trigger.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

        // Focus lands on the real button so the keyboard carries on from there
        // — but not while it is disabled, where the browser would drop focus to
        // the page and lose the reader's place. preventScroll leaves the glide
        // above to do the moving.
        if (!this.trigger.disabled) this.trigger.focus({ preventScroll: true });
      }

      toggle() {
        const visible = this.pastTrigger && !this.atStop;
        this.classList.toggle('sticky-atc--visible', visible);
        // Hidden it is still in the page, and a button nobody can see is not a
        // tab stop worth having.
        this.inert = !visible;
        this.setAttribute('aria-hidden', String(!visible));
      }

      sync() {
        if (this.priceSource && this.priceTarget) {
          const price = this.priceSource.innerHTML;
          if (price !== this.lastPrice) {
            this.priceTarget.innerHTML = price;
            this.lastPrice = price;
          }
          this.priceTarget.classList.toggle('hidden', this.priceSource.classList.contains('hidden'));
        }

        if (this.variantText) {
          // The picker rewrites these as options are chosen, which is a step
          // ahead of the variant the server later confirms — the same reading
          // the labels above the pickers show.
          const chosen = Array.from(this.source.querySelectorAll('[data-selected-value]'))
            .map((element) => element.textContent.trim())
            .filter(Boolean);
          this.variantText.textContent = chosen.length ? ` · ${chosen.join(' / ')}` : '';
        }

        // The label follows the real button — "Sold out" there reads "Sold out"
        // here — but the state does not: this one only ever scrolls, so there is
        // never a moment when pressing it is the wrong thing to do.
        const label = this.trigger.querySelector('span')?.textContent.trim();
        if (label) this.buttonText.textContent = label;
      }
    }
  );
}
